import { Box, Divider, Link, Typography } from '@mui/material';
import Spinner from 'components/Common/Spinner';
import { useHttp } from 'hooks/use-fetch';
import { fetchLabListReport } from 'pages/Dashboard/services/labs.services';
import {
  ShowMoreDrawerChildProps,
  sliceForShowMore,
} from 'pages/Dashboard/ShowMoreDrawer';
import { LabReport, LabReportAPIResponse } from 'pages/Dashboard/types/lab.types';
import * as React from 'react';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import theme from 'theme';
import { googleDateToDateString } from 'utils/dateUtils';
import { downloadUrl } from 'utils/service';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import NoHealthRecordDataCard, { HealthRecordCategory } from 'components/NoHealthRecordDataCard';
import PatientDetailsCard from 'components/PatientDetailsCard';

export interface LabCardProps extends ShowMoreDrawerChildProps {
  gridSize?: number;
  labReports?: LabReport[];
  isNewUi?: boolean;
}

interface LabDetailsProps {
  labData: LabReport[];
  reportResponseIgnite: boolean;
  fetchPdf: (labId: string) => Promise<void>;
}

function LabDetails({
  labData,
  reportResponseIgnite,
  fetchPdf,
}: LabDetailsProps): JSX.Element {
  const datedLabReports: LabReport[] = [];
  const nonDatedLabReports: LabReport[] = [];
  labData.forEach((lab) => {
    if (lab.date) {
      datedLabReports.push(lab);
    }
    else {
      nonDatedLabReports.push(lab);
    }
  });
  return (
    <>
      {datedLabReports.map((report, idx) => (
        <>
          <Box
            key={`lab-${report.id}`}
            display='flex'
            flexDirection='row'
            justifyContent='space-between'
            alignItems='flex-start'
            width='100%'
            py={1.5}
          >
            {/* Left side: lab name, date, doctor */}
            <Box display='flex' flexDirection='column' flex={1}>
              <Typography variant='body1' color='text.primary'>
                {report?.lab?.name}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {googleDateToDateString(report.date)}
              </Typography>
              {report.doctor && (
              <Typography variant='body2' color='text.secondary'>
                {`${report.doctor.firstName} ${report.doctor.lastName}`}
              </Typography>
              )}
            </Box>
            {/* Right side: clickable report */}
            <Box display='flex' flexDirection='column' alignItems='flex-end' flex={1}>
              <Link
                component='a'
                variant='body2'
                underline='hover'
                textAlign='right'
                sx={{ cursor: 'pointer', color: theme.custom.colors.linkBlue, fontWeight: 500 }}
                onClick={() => {
                  trackMixpanelEvent(MixpanelEventName.LAB_REPORT_PDF_CLICKED, {
                    lab_report_id: report.id,
                  });
                  fetchPdf(report.id);
                }}
              >
                {report.name}
                {' '}
                (PDF)
              </Link>
            </Box>
          </Box>
          {idx < datedLabReports.length - 1 && (
          <Divider sx={{ borderColor: theme.custom.colors.borderColor }} />
          )}
        </>
      ))}
      {nonDatedLabReports.length ? (
        <>
          <Typography mb={2} variant='subtitle1'>
            Unknown dates
          </Typography>
          {nonDatedLabReports.map((report, idx) => (
            <>
              <Box
                key={`lab-${report.id}`}
                display='flex'
                flexDirection='row'
                justifyContent='space-between'
                alignItems='flex-start'
                width='100%'
                py={1.5}
              >
                {/* Left side: lab name, doctor */}
                <Box display='flex' flexDirection='column'>
                  <Typography variant='body1' color='text.primary'>
                    {report?.lab?.name}
                  </Typography>
                  {report.doctor && (
                    <Typography variant='body2' color='text.secondary'>
                      {`${report.doctor.firstName} ${report.doctor.lastName}`}
                    </Typography>
                  )}
                </Box>
                {/* Right side: clickable report */}
                <Box display='flex' flexDirection='column' alignItems='flex-end'>
                  {reportResponseIgnite ? (
                    <Spinner color='secondary' />
                  ) : (
                    <Link
                      component='a'
                      variant='body2'
                      underline='hover'
                      sx={{ cursor: 'pointer', color: theme.custom.colors.linkBlue, fontWeight: 500 }}
                      onClick={() => {
                        trackMixpanelEvent(MixpanelEventName.LAB_REPORT_PDF_CLICKED, {
                          lab_report_id: report.id,
                        });
                        fetchPdf(report.id);
                      }}
                    >
                      {report.name}
                    </Link>
                  )}
                </Box>
              </Box>
              {idx < nonDatedLabReports.length - 1 && (
                <Divider sx={{ borderColor: theme.custom.colors.borderColor }} />
              )}
            </>
          ))}
        </>
      ) : null}
    </>
  );
}

export default function LabCard({
  gridSize,
  drawerMode = false,
  labReports = [],
  isNewUi = false,
  onShowMore,
}: LabCardProps): JSX.Element | null {
  const { id } = useParams();
  const { http, providerId } = useHttp();
  const [fetchLabReportResponse, setFetchLabReportResponse] = React.useState<LabReportAPIResponse>(
    {} as LabReportAPIResponse,
  );

  const datedLabReports = labReports.filter((labs) => labs.date);
  const nonDatedLabReports = labReports
    .filter((labs) => !labs.date)
    .sort((a, b) => a.lab.name.localeCompare(b.lab.name));

  const sortedLabReports = [...datedLabReports, ...nonDatedLabReports];
  const data = sliceForShowMore(sortedLabReports, !drawerMode);

  const { mutate: fetchLabReport, isLoading } = useMutation({
    mutationFn: (labId: string) => fetchLabListReport(http.post, providerId, id as string, labId),
    onSettled: (d) => {
      setFetchLabReportResponse(d);
    },
  });

  React.useEffect(() => {
    if (fetchLabReportResponse?.signed_url && !isLoading) {
      downloadUrl(fetchLabReportResponse?.signed_url, '', true);
    }
  }, [fetchLabReportResponse?.signed_url, isLoading]);

  const fetchPdf = async (labId: string) => {
    fetchLabReport(labId);
  };

  return (
    <PatientDetailsCard
      title='Labs'
      showHeader={!drawerMode && labReports.length > 0}
      gridProps={{ xs: 12, md: !drawerMode ? gridSize : undefined }}
      viewDetailsBtnLabel='View all'
      showViewDetails={!drawerMode && data.length > 3 && !!onShowMore}
      onViewDetails={onShowMore as VoidFunction}
      key='labs'
      showSubHeader={false}
    >
      {labReports.length === 0 && isNewUi ? (
        <NoHealthRecordDataCard
          title='No labs found'
          description='Data will be available here once there is a activity to record'
          healthRecordCategory={HealthRecordCategory.labs}
        />
      ) : (
        <LabDetails
          labData={data}
          reportResponseIgnite={isLoading || false}
          fetchPdf={fetchPdf}
        />
      )}
    </PatientDetailsCard>
  );
}
